import { entityType } from '@trade-platform/lib-enums';
import { getFromStorage } from '@trade-platform/ui-utils';
import { constants } from '../../utils/constants';
import { Profile } from './model';

export const isActiveInvestor = (profile: Profile) => {
    return !!profile.roles.find(role => role.name === constants.profileTypes.ACTIVE_INVESTOR);
};

/**
 * AIX staff, including the integration and demo accounts. Roles cannot tell staff apart, because
 * `sysAdmin` is also a client-tenant role, so the email domain is the signal.
 */
const INTERNAL_EMAIL_DOMAINS = ['aixplatform.com', 'investwithaqua.com'];

export const isInternalEmail = (email: string | null | undefined): boolean => {
    const domain = (email ?? '').trim().toLowerCase().split('@').pop();
    return INTERNAL_EMAIL_DOMAINS.includes(domain ?? '');
};

/**
 * Whether analytics (PostHog identify and session replay) leave out this user's name and email.
 * Only a pure investor login is withheld: every role is `activeInvestor` and the email is not an AIX
 * staff address. Any other role makes the user a business user of the platform. Takes any user with
 * roles and an email, such as the signed-in profile or a row in an admin users list.
 */
export const hidesIdentityFromAnalytics = (profile: {
    roles: Pick<Profile['roles'][number], 'name'>[];
    email?: string | null;
}): boolean =>
    profile.roles.length > 0 &&
    profile.roles.every(role => role.name === constants.profileTypes.ACTIVE_INVESTOR) &&
    !isInternalEmail(profile.email);

export const isFeatureEnabled = (feature: string) => {
    const profile: Profile | null | undefined = getFromStorage('profile');

    if (!profile || !profile.features) {
        return false;
    }

    const features: string[] = profile.features.map(feat => feat.name);
    return features.indexOf(feature) > -1;
};

export const hasEntityType = (type: string) => {
    const profile: Profile | null | undefined = getFromStorage('profile');

    if (
        (type === entityType.firm && profile?.firm) ||
        (type === entityType.holdingOption && profile?.holdingOption) ||
        (type === entityType.fundSponsor && profile?.fundSponsor)
    ) {
        return true;
    } else {
        return false;
    }
};

export const hasOrganizationType = (type: string) => {
    const profile: Profile | null | undefined = getFromStorage('profile');

    return profile?.organization?.type === type;
};

export const hasRoles = (roles: string[]) => {
    const profile: Profile | null | undefined = getFromStorage('profile');

    if (profile) {
        return (profile.roles ?? []).some(role => roles.includes(role.name));
    }
    return false;
};
